import React from 'react';
import { DrawerBody, OverlayDrawer } from '@fluentui/react-components';
import {
    CheckboxChecked16Filled,
    CheckboxUnchecked16Regular,
    ChevronDown12Regular,
    Dismiss20Regular,
    Search16Regular,
} from '@fluentui/react-icons';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import axiosInstance from '../../apiServices/axios';
import { AI_WARDEN_DOCS_LIGHTWEIGHT, aiWardenWorkstreamQuery } from '../../apiServices/api_routes';
import { GetWorkstreams } from '../../redux/dataroomSlice';
import { GetInformationRequests } from '../../redux/questionBankSlice';
import { GetObservationsByScope } from '../../redux/meetingSlice';
import { CreateTaskLinkedRecord } from '../../redux/taskSlice';
import { htmlToPlainText } from '../../utils/commonUtils';
import styles from './css/index.module.css';

const TABS = [
    { id: 'vdr_file', label: 'VDR Files' },
    { id: 'irl', label: 'IRLs' },
    { id: 'observation', label: 'Observations' },
];

const stripHtml = text => {
    if (!text) return '';
    return String(htmlToPlainText(text) || '')
        .replace(/\s+/g, ' ')
        .trim();
};

const irlNumber = item => `${item?.prefix || ''}${item?.tag || ''}.${item?.item}.${item?.sub_item}`;

const TaskReferencePicker = ({ open, onClose, taskId, existingLinks = [] }) => {
    const dispatch = useDispatch();
    const { workstreams } = useSelector(state => state.dataroom);
    const { informationRequests } = useSelector(state => state.questionBank);
    const { scopeObservations } = useSelector(state => state.meeting);

    const [tab, setTab] = React.useState('vdr_file');
    const [search, setSearch] = React.useState('');
    const [selectedWorkstreamId, setSelectedWorkstreamId] = React.useState('');
    const [vdrDocs, setVdrDocs] = React.useState([]);
    const [loadingDocs, setLoadingDocs] = React.useState(false);
    const [selected, setSelected] = React.useState([]);
    const [submitting, setSubmitting] = React.useState(false);

    const selectedWorkstream = React.useMemo(
        () => (workstreams || []).find(ws => String(ws.id) === String(selectedWorkstreamId)),
        [workstreams, selectedWorkstreamId],
    );

    const linkedIds = React.useMemo(() => {
        const ids = { vdr_file: new Set(), irl: new Set(), observation: new Set() };
        (existingLinks || []).forEach(link => {
            if (link?.record_type && link?.record_id != null) {
                ids[link.record_type]?.add(Number(link.record_id));
            }
        });
        return ids;
    }, [existingLinks]);

    React.useEffect(() => {
        if (!open) return;
        setTab('vdr_file');
        setSearch('');
        setSelected([]);
        if (!workstreams?.length) dispatch(GetWorkstreams());
        dispatch(GetInformationRequests());
        dispatch(GetObservationsByScope('all'));
    }, [open, dispatch]);

    React.useEffect(() => {
        if (!open || !workstreams?.length) return;
        if (!selectedWorkstreamId) {
            const saved = localStorage['workstream'];
            const match = workstreams.find(ws => String(ws.id) === String(saved));
            setSelectedWorkstreamId(String(match?.id || workstreams[0].id));
        }
    }, [open, workstreams, selectedWorkstreamId]);

    React.useEffect(() => {
        if (!open || tab !== 'vdr_file' || !selectedWorkstreamId) return;
        let cancelled = false;
        const load = async () => {
            setLoadingDocs(true);
            try {
                const projectId = localStorage['project_id'];
                const q = aiWardenWorkstreamQuery([selectedWorkstreamId]);
                const { data } = await axiosInstance.get(`${AI_WARDEN_DOCS_LIGHTWEIGHT(projectId)}${q}`);
                const docs = Array.isArray(data?.documents) ? data.documents : Array.isArray(data) ? data : [];
                if (!cancelled) setVdrDocs(docs);
            } catch (err) {
                if (!cancelled) {
                    setVdrDocs([]);
                    toast.error('Failed to load VDR files');
                }
            } finally {
                if (!cancelled) setLoadingDocs(false);
            }
        };
        load();
        return () => {
            cancelled = true;
        };
    }, [open, tab, selectedWorkstreamId]);

    const query = search.trim().toLowerCase();

    const rows = React.useMemo(() => {
        if (tab === 'vdr_file') {
            return (vdrDocs || [])
                .filter(doc => !linkedIds.vdr_file.has(Number(doc.id)))
                .filter(doc => {
                    if (!query) return true;
                    const name = (doc.document_name || doc.name || '').toLowerCase();
                    const folder = (doc.ws_folder || doc.folder_name || '').toLowerCase();
                    return name.includes(query) || folder.includes(query);
                })
                .map(doc => ({
                    key: `vdr-${doc.id}`,
                    id: doc.id,
                    record_type: 'vdr_file',
                    label: doc.document_name || doc.name || `File ${doc.id}`,
                    subtitle: doc.ws_folder || doc.folder_name || '',
                }));
        }
        if (tab === 'irl') {
            return (informationRequests || [])
                .filter(item => !linkedIds.irl.has(Number(item.id)))
                .filter(item => {
                    if (!selectedWorkstreamId) return true;
                    return String(item.workstream) === String(selectedWorkstreamId);
                })
                .filter(item => {
                    if (!query) return true;
                    const num = irlNumber(item).toLowerCase();
                    const req = stripHtml(item.request).toLowerCase();
                    return num.includes(query) || req.includes(query);
                })
                .map(item => ({
                    key: `irl-${item.id}`,
                    id: item.id,
                    record_type: 'irl',
                    label: irlNumber(item),
                    subtitle: stripHtml(item.request),
                }));
        }
        return (scopeObservations || [])
            .filter(item => !linkedIds.observation.has(Number(item.id)))
            .filter(item => {
                if (!selectedWorkstream?.name) return true;
                return item.workstream_repr === selectedWorkstream.name;
            })
            .filter(item => {
                if (!query) return true;
                return stripHtml(item.observation).toLowerCase().includes(query);
            })
            .map(item => ({
                key: `obs-${item.id}`,
                id: item.id,
                record_type: 'observation',
                label: stripHtml(item.observation) || `Observation ${item.id}`,
                subtitle: [item.assessment, item.workstream_repr].filter(Boolean).join(' · '),
            }));
    }, [tab, vdrDocs, informationRequests, scopeObservations, linkedIds, query, selectedWorkstreamId, selectedWorkstream]);

    const toggleSelect = row => {
        setSelected(prev => {
            const exists = prev.find(item => item.key === row.key);
            if (exists) return prev.filter(item => item.key !== row.key);
            return [...prev, row];
        });
    };

    const handleAdd = async () => {
        if (!taskId || selected.length === 0) return;
        setSubmitting(true);
        try {
            for (const row of selected) {
                const body = { task: taskId, record_type: row.record_type };
                if (row.record_type === 'vdr_file') body.document = row.id;
                if (row.record_type === 'irl') body.information_request = row.id;
                if (row.record_type === 'observation') body.observation = row.id;
                await dispatch(CreateTaskLinkedRecord(body)).unwrap();
            }
            onClose?.();
        } catch {
            // toast handled in slice
        } finally {
            setSubmitting(false);
        }
    };

    const handleWorkstreamChange = e => {
        setSelectedWorkstreamId(e.target.value);
        setSelected([]);
    };

    return (
        <OverlayDrawer
            style={{ minWidth: 400, width: 400 }}
            type="overlay"
            position="end"
            open={open}
            onOpenChange={(_, { open: isOpen }) => {
                if (!isOpen) onClose?.();
            }}>
            <DrawerBody className={styles.referencePickerDrawerBody}>
                <div className={styles.referencePickerShell}>
                    <div className={styles.referencePickerHeader}>
                        <h5 className={styles.referencePickerTitle}>Add reference</h5>
                        <button type="button" className={styles.referencePickerClose} onClick={onClose} aria-label="Close">
                            <Dismiss20Regular />
                        </button>
                    </div>

                    <div className={styles.referencePickerTabs}>
                        {TABS.map(item => (
                            <button
                                key={item.id}
                                type="button"
                                className={`${styles.referencePickerTab} ${tab === item.id ? styles.referencePickerTabActive : ''}`}
                                onClick={() => {
                                    setTab(item.id);
                                    setSelected([]);
                                    setSearch('');
                                }}>
                                {item.label}
                            </button>
                        ))}
                    </div>

                    <div className={styles.referencePickerFilters}>
                        <div className={styles.referencePickerSelectWrap}>
                            <select
                                className={styles.referencePickerSelect}
                                value={selectedWorkstreamId}
                                onChange={handleWorkstreamChange}
                                aria-label="Workstream">
                                {(workstreams || []).map(ws => (
                                    <option key={ws.id} value={ws.id}>
                                        {ws.name}
                                    </option>
                                ))}
                            </select>
                            <ChevronDown12Regular className={styles.referencePickerSelectChevron} />
                        </div>

                        <div className={styles.referencePickerSearchWrap}>
                            <Search16Regular className={styles.referencePickerSearchIcon} />
                            <input
                                type="search"
                                className={styles.referencePickerSearch}
                                placeholder="Search..."
                                value={search}
                                onChange={e => setSearch(e.target.value)}
                            />
                        </div>
                    </div>

                    <div className={styles.referencePickerList}>
                        {tab === 'vdr_file' && loadingDocs ? (
                            <p className={styles.referencePickerEmpty}>Loading files…</p>
                        ) : rows.length === 0 ? (
                            <p className={styles.referencePickerEmpty}>No records found</p>
                        ) : (
                            rows.map(row => {
                                const isSelected = selected.some(item => item.key === row.key);
                                return (
                                    <button
                                        key={row.key}
                                        type="button"
                                        className={`${styles.referencePickerRow} ${isSelected ? styles.referencePickerRowSelected : ''}`}
                                        onClick={() => toggleSelect(row)}>
                                        <span className={styles.referencePickerCheckbox} aria-hidden>
                                            {isSelected ? (
                                                <CheckboxChecked16Filled color="var(--Brand-Secondary, #8E2DE2)" />
                                            ) : (
                                                <CheckboxUnchecked16Regular color="#C6C8D2" />
                                            )}
                                        </span>
                                        <span className={styles.referencePickerRowMeta}>
                                            <span className={styles.referencePickerRowLabel} title={row.label}>
                                                {row.label}
                                            </span>
                                            {row.subtitle ? (
                                                <span className={styles.referencePickerRowSubtitle} title={row.subtitle}>
                                                    {row.subtitle}
                                                </span>
                                            ) : null}
                                        </span>
                                    </button>
                                );
                            })
                        )}
                    </div>

                    <div className={styles.referencePickerFooter}>
                        <span className={styles.referencePickerCount}>{selected.length} selected</span>
                        <div className={styles.referencePickerActions}>
                            <button type="button" className={styles.referencePickerCancelBtn} onClick={onClose}>
                                Cancel
                            </button>
                            <button
                                type="button"
                                className={styles.referencePickerAddBtn}
                                disabled={selected.length === 0 || submitting}
                                onClick={handleAdd}>
                                {submitting ? 'Adding…' : 'Add'}
                            </button>
                        </div>
                    </div>
                </div>
            </DrawerBody>
        </OverlayDrawer>
    );
};

export default TaskReferencePicker;
